// resources/js/src/components/community/components/profile/AllPhotosModal.tsx
import { Fragment, useEffect, useCallback, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Icon } from "@iconify/react";
import { profileApi } from "../../../../../services/profile.api";
import type { ProfilePhoto } from "../../../../../services/profile.api";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  initialPhotos?: ProfilePhoto[];
};

export default function AllPhotosModal({
  isOpen,
  onClose,
  userId,
  initialPhotos = [],
}: Props) {
  const [photos, setPhotos] = useState<ProfilePhoto[]>(initialPhotos);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<ProfilePhoto | null>(null);

  // ===== Reset on open =====
  useEffect(() => {
    if (!isOpen) return;
    setPhotos(initialPhotos);
    setCursor(null);
    setHasMore(true);
    setError("");
    setPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  // ===== Load more =====
  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    setError("");
    try {
      const res = await profileApi.getPhotos(userId, cursor, 15);
      setPhotos((prev) => [...prev, ...res.data]);
      setCursor(res.meta.next_cursor);
      setHasMore(res.meta.has_more);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load photos");
    } finally {
      setLoading(false);
    }
  }, [userId, cursor, hasMore, loading]);

  // ===== Initial load =====
  useEffect(() => {
    if (isOpen && photos.length === 0) loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ===== Close Lightbox on Escape =====
  useEffect(() => {
    if (!preview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPreview(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview]);

  return (
    <>
      {/* ==================== Main Dialog ==================== */}
      <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </Transition.Child>

          {/* Container */}
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-4xl max-h-[85vh] flex flex-col rounded-2xl bg-white dark:bg-deepSlate shadow-2xl overflow-hidden">
                {/* ===== Header ===== */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                  <div className="flex items-center gap-2">
                    <Icon
                      icon="mdi:image-multiple-outline"
                      className="text-primary-600 dark:text-primary-400 text-xl"
                    />
                    <Dialog.Title className="text-lg font-bold text-gray-900 dark:text-white">
                      All Photos
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={onClose}
                    className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 active:scale-[0.97]"
                    aria-label="Close"
                  >
                    <Icon icon="mdi:close" className="text-xl" />
                  </button>
                </div>

                {/* ===== Body ===== */}
                <div className="flex-1 overflow-y-auto p-5">
                  {photos.length === 0 && !loading && (
                    <div className="flex flex-col items-center justify-center py-16">
                      <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
                        <Icon
                          icon="mdi:image-off-outline"
                          className="text-gray-400 text-3xl"
                        />
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        No photos yet.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {photos.map((photo) => (
                      <button
                        key={photo.id}
                        onClick={() => setPreview(photo)}
                        className="aspect-square relative overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-800 group ring-0 hover:ring-2 hover:ring-primary-500 transition-all duration-200"
                      >
                        <img
                          src={photo.url}
                          alt="Photo"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
                          <Icon
                            icon="mdi:magnify-plus-outline"
                            className="text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          />
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Loading */}
                  {loading && (
                    <div className="flex justify-center py-6">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                    </div>
                  )}

                  {/* Error */}
                  {error && (
                    <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50">
                      <Icon
                        icon="mdi:alert-circle-outline"
                        className="text-red-500 text-lg shrink-0"
                      />
                      <p className="text-xs text-red-600 dark:text-red-400">
                        {error}
                      </p>
                    </div>
                  )}

                  {/* Load More */}
                  {hasMore && !loading && (
                    <button
                      onClick={loadMore}
                      className="w-full mt-5 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-all duration-200 active:scale-[0.97]"
                    >
                      <Icon icon="mdi:chevron-down" className="text-base" />
                      Load More
                    </button>
                  )}

                  {/* End */}
                  {!hasMore && photos.length > 0 && (
                    <p className="text-center text-gray-400 dark:text-gray-500 text-xs mt-5">
                      You've reached the end.
                    </p>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* ==================== Lightbox ==================== */}
      <Transition show={!!preview} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[60]"
          onClose={() => setPreview(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/90 backdrop-blur-md" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-90"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-90"
            >
              <Dialog.Panel className="relative max-w-full max-h-full">
                <button
                  onClick={() => setPreview(null)}
                  className="absolute -top-12 right-0 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-all duration-200"
                  aria-label="Close preview"
                >
                  <Icon icon="mdi:close" className="text-2xl" />
                </button>

                {preview && (
                  <img
                    src={preview.url}
                    alt="Preview"
                    className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
                  />
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}